import { useCallback, useEffect, useRef, useState } from "react";
import { classifyAiMappingError } from "./aiMappingErrors";
import { getPricingGroupCode } from "./aiMappingColors";
import { loadAiMeasurementFeatureIds } from "./aiMappingStorage";
import { useCreateAiMappingRun } from "./hooks";
import {
  AiMappingActiveTask,
  AiMappingController,
  UseAiMappingControllerOptions,
} from "./aiMappingControllerTypes";
import { useAiMappingAreaTasks } from "./useAiMappingAreaTasks";
import { useAiMappingDetectionHandlers } from "./useAiMappingDetectionHandlers";
import { useAiMappingSegmentationHandlers } from "./useAiMappingSegmentationHandlers";
import { useAiMappingAssignmentSync } from "./useAiMappingAssignmentSync";
import { useAiMappingRun } from "./useAiMappingRun";
import { useAiMappingResults } from "./useAiMappingResults";
import { useAiMappingAreaDrawing } from "./useAiMappingAreaDrawing";
import { useAiMappingMapSync } from "./useAiMappingMapSync";
import { useAiMappingMeasuredAreaTools } from "./useAiMappingMeasuredAreaTools";
import { AiMappingTaskSnapshot } from "./aiMappingTask";

export type {
  AiMappingNotify,
  AiMappingTaskKind,
  AiMappingActiveTask,
  AiMappingDrawSession,
  UseAiMappingControllerOptions,
  AiMappingSourceSyncItem,
  AiMappingController,
} from "./aiMappingControllerTypes";

export default function useAiMappingController({
  projectMapId,
  driver,
  enabled,
  notify,
  pollIntervalMs,
  defaultAreaName,
  existingMeasurementIds,
  onSplitLine,
  onErasePolygon,
  onMergePolygon,
}: UseAiMappingControllerOptions): AiMappingController {
  const [unauthorized, setUnauthorized] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<AiMappingActiveTask | null>(null);
  const [busy, setBusy] = useState(false);
  const [taskSnapshot, setTaskSnapshot] = useState<AiMappingTaskSnapshot | null>(null);
  const [selectedDetectionId, setSelectedDetectionId] = useState<string | null>(null);
  const [detectionsVisible, setDetectionsVisible] = useState(true);
  const [detectionGroupVisibility, setDetectionGroupVisibility] = useState<Record<string, boolean>>(
    {}
  );
  const [segmentationsVisible, setSegmentationsVisible] = useState(true);
  const [selectedSegmentationId, setSelectedSegmentationId] = useState<string | null>(null);
  const [checkedSegmentationIds, setCheckedSegmentationIds] = useState<string[]>([]);
  const [quickAddCategory, setQuickAddCategory] = useState<string | null>(null);
  const [quickAddAreaId, setQuickAddAreaId] = useState<string | null>(null);
  const [splittingSegmentationId, setSplittingSegmentationId] = useState<string | null>(null);
  const [addingSegmentationAreaId, setAddingSegmentationAreaId] = useState<string | null>(null);
  const [aiMeasurementIds, setAiMeasurementIds] = useState<string[]>(() =>
    projectMapId ? loadAiMeasurementFeatureIds(projectMapId) : []
  );

  const unmountedRef = useRef(false);
  const measuredAreaToolsRef = useRef<{ cancelModes: () => void } | null>(null);
  const detectRunIdRef = useRef(0);
  const quickAddQueueRef = useRef<Promise<void>>(Promise.resolve());
  const assignmentSyncQueueRef = useRef<Promise<void>>(Promise.resolve());
  const sleepTimerRef = useRef<{
    timeout: ReturnType<typeof setTimeout>;
    resolve: () => void;
  } | null>(null);

  const sleep = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      sleepTimerRef.current = { timeout: setTimeout(resolve, ms), resolve };
    });
  }, []);

  const cancelSleep = useCallback(() => {
    if (sleepTimerRef.current) {
      clearTimeout(sleepTimerRef.current.timeout);
      sleepTimerRef.current.resolve();
      sleepTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      detectRunIdRef.current += 1;
      cancelSleep();
    };
  }, [cancelSleep]);

  const setFormErrorRef = useRef<((value: string | null) => void) | null>(null);

  const handleError = useCallback(
    (error: unknown, { inline }: { inline?: boolean } = {}): boolean => {
      const info = classifyAiMappingError(error);
      if (info.code === "unauthorized") {
        setUnauthorized(true);
        return true;
      }
      if (inline && info.code === "validation") {
        setFormErrorRef.current?.(info.detail ?? info.message);
        return true;
      }
      notify(info.detail ? `${info.message} (${info.detail})` : info.message, "error");
      return true;
    },
    [notify]
  );

  const runState = useAiMappingRun({
    projectMapId,
    unmountedRef,
    notify,
    handleError,
    setUnauthorized,
  });
  const { run, areas } = runState;

  const resultsState = useAiMappingResults({
    projectMapId,
    unmountedRef,
    areas,
    notify,
    handleError,
    refreshRun: runState.refreshRun,
  });

  const selectArea = useCallback(
    async (areaId: string | null, options?: { keepSegmentation?: boolean }) => {
      setSelectedDetectionId(null);
      if (!options?.keepSegmentation) {
        setSelectedSegmentationId(null);
      }
      setCheckedSegmentationIds([]);
      setQuickAddCategory(null);
      setQuickAddAreaId(null);
      setAddingSegmentationAreaId(null);
      measuredAreaToolsRef.current?.cancelModes();
      driver?.clearDraft();
      driver?.setClickHandler(null);
      if (!areaId) {
        setSelectedAreaId(null);
        resultsState.setResults(null);
        return;
      }
      setSelectedAreaId(areaId);
      const loaded = resultsState.allResults.find((result) => result.area.id === areaId);
      if (loaded) {
        resultsState.setResults(loaded);
        return;
      }
      resultsState.setResultsLoading(true);
      await resultsState.loadAreaResults(areaId);
      resultsState.setResultsLoading(false);
    },
    [driver]
  );

  const drawingState = useAiMappingAreaDrawing({
    run,
    driver,
    defaultAreaName,
    unmountedRef,
    notify,
    handleError,
    updateAreaInRun: runState.updateAreaInRun,
    onAreaCreated: (areaId) => {
      setSelectedAreaId(areaId);
    },
  });

  setFormErrorRef.current = drawingState.setFormError;

  useEffect(() => {
    detectRunIdRef.current += 1;
    runState.reset();
    resultsState.reset();
    selectArea(null);
    setUnauthorized(false);
    setActiveTask(null);
    setTaskSnapshot(null);
    setDetectionGroupVisibility({});
    setDetectionsVisible(true);
    setSegmentationsVisible(true);
    setSelectedDetectionId(null);
    setSelectedSegmentationId(null);
    setCheckedSegmentationIds([]);
    setQuickAddCategory(null);
    setQuickAddAreaId(null);
    setSplittingSegmentationId(null);
    setAddingSegmentationAreaId(null);
    setAiMeasurementIds(projectMapId ? loadAiMeasurementFeatureIds(projectMapId) : []);
  }, [projectMapId]);

  useEffect(() => {
    if (
      enabled &&
      projectMapId &&
      !run &&
      !runState.loading &&
      !runState.loadAttemptedRef.current
    ) {
      runState.loadAttemptedRef.current = true;
      runState.loadRun();
    }
  }, [enabled, projectMapId, run, runState]);

  const { detectArea, segmentArea, exportPdf, cancelTask } = useAiMappingAreaTasks({
    activeTask,
    pollIntervalMs,
    unmountedRef,
    detectRunIdRef,
    setActiveTask,
    setTaskSnapshot,
    updateAreaInRun: runState.updateAreaInRun,
    applyResults: resultsState.applyResults,
    refreshRun: runState.refreshRun,
    getAreaById: runState.getAreaById,
    sleep,
    cancelSleep,
    notify,
    handleError,
  });

  const {
    updateDetection,
    deleteDetection,
    undoArea,
    redoArea,
    selectDetection,
    cancelQuickAdd,
    toggleQuickAdd,
    refetchResults,
  } = useAiMappingDetectionHandlers({
    busy,
    quickAddCategory,
    quickAddAreaId,
    unmountedRef,
    quickAddQueueRef,
    setBusy,
    setQuickAddCategory,
    setQuickAddAreaId,
    setSelectedDetectionId,
    applyResults: resultsState.applyResults,
    refetchResults: resultsState.refetchResults,
    getAreaById: runState.getAreaById,
    driver,
    notify,
    handleError,
  });

  const {
    selectSegmentation,
    toggleSegmentationChecked,
    updateSegmentation,
    deleteSegmentation,
    mergeSegmentations,
    splitSegmentation,
    splitAreasAlongLine,
    eraseAreasAlongPolygon,
    mergeAreasAlongPolygon,
    startAddSegmentation,
  } = useAiMappingSegmentationHandlers({
    busy,
    activeTask,
    allResults: resultsState.allResults,
    unmountedRef,
    setBusy,
    setSelectedSegmentationId,
    setCheckedSegmentationIds,
    setSplittingSegmentationId,
    setAddingSegmentationAreaId,
    applyResults: resultsState.applyResults,
    refetchResults: resultsState.refetchResults,
    getAreaById: runState.getAreaById,
    driver,
    notify,
    handleError,
  });

  const { syncAiSourceToAreas } = useAiMappingAssignmentSync({
    projectMapId,
    existingMeasurementIds,
    aiMeasurementIds,
    unmountedRef,
    handleError,
    setAiMeasurementIds,
  });

  const measuredAreaTools = useAiMappingMeasuredAreaTools({
    driver,
    notify,
    resetKey: projectMapId ?? null,
    splittingSegmentationId,
    setSplittingSegmentationId,
    selectedAreaId,
    selectedSegmentationId,
    allResults: resultsState.allResults,
    selectArea,
    selectSegmentation,
    splitSegmentation,
    splitAreasAlongLine,
    eraseAreasAlongPolygon,
    mergeAreasAlongPolygon,
    onSplitLine,
    onErasePolygon,
    onMergePolygon,
  });
  measuredAreaToolsRef.current = measuredAreaTools;

  const isDetectionGroupVisible = useCallback(
    (category: string) => detectionGroupVisibility[getPricingGroupCode(category)] !== false,
    [detectionGroupVisibility]
  );
  const toggleDetectionGroupVisible = useCallback((category: string) => {
    const group = getPricingGroupCode(category);
    setDetectionGroupVisibility((current) => ({ ...current, [group]: current[group] === false }));
  }, []);

  useAiMappingMapSync({
    driver,
    areas,
    selectedAreaId,
    allResults: resultsState.allResults,
    detectionsVisible,
    segmentationsVisible,
    selectedDetectionId,
    selectedSegmentationId,
    isDetectionGroupVisible,
    selectArea,
    selectDetection,
    selectSegmentation,
    onSegmentationClick: measuredAreaTools.onSegmentationClick,
  });

  return {
    run,
    loading: runState.loading,
    loadError: runState.loadError,
    unauthorized,
    areas,
    selectedAreaId,
    drawSession: drawingState.drawSession,
    savingArea: drawingState.savingArea,
    formError: drawingState.formError,
    activeTask,
    busy,
    taskSnapshot,
    results: resultsState.results,
    allResults: resultsState.allResults,
    defaultAreaName: defaultAreaName ?? null,
    aiMeasurementIds,
    selectedDetectionId,
    detectionsVisible,
    segmentationsVisible,
    quickAddCategory,
    quickAddAreaId,
    toggleQuickAdd,
    cancelQuickAdd,
    selectSegmentation,
    selectedSegmentationId,
    checkedSegmentationIds,
    toggleSegmentationChecked,
    updateSegmentation,
    deleteSegmentation,
    mergeSegmentations,
    splitSegmentation,
    splittingSegmentationId: measuredAreaTools.splittingSegmentationId,
    startAddSegmentation,
    addingSegmentationAreaId,
    splitModeActive: measuredAreaTools.splitModeActive,
    mergeModeActive: measuredAreaTools.mergeModeActive,
    toggleMergeMode: measuredAreaTools.toggleMergeMode,
    eraseModeActive: measuredAreaTools.eraseModeActive,
    toggleEraseMode: measuredAreaTools.toggleEraseMode,
    toggleSplitMode: measuredAreaTools.toggleSplitMode,
    splitAreasAlongLine,
    startDrawing: drawingState.startDrawing,
    cancelDrawing: drawingState.cancelDrawing,
    saveArea: drawingState.saveArea,
    selectArea,
    detectArea,
    segmentArea,
    exportPdf,
    cancelTask,
    refreshRun: runState.refreshRun,
    retryLoadRun: runState.retryLoadRun,
    updateDetection,
    deleteDetection,
    undoArea,
    redoArea,
    selectDetection,
    setDetectionsVisible,
    isDetectionGroupVisible,
    toggleDetectionGroupVisible,
    setSegmentationsVisible,
    syncAiSourceToAreas,
  };
}
